// deps
import React, { memo, ReactElement, useRef } from "react";
import cx from "classnames";

// components
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";

// helpers
import { BottomSheet, BottomSheetChildrenProps, getCloudinaryUrl } from "@swiggy-private/core-ui";

//interfaces
import { useElapsedTime } from "@payment-ui/hooks/useElapsedTime";

import styles from "./styles.scss";

// test-ids
import { SUCCESS_BS_REDIRECT_TEST_IDS } from "@swiggy-private/payment-test-ids";

const ICON_WIDTH = 80;

interface SuccessProps {
    title: string;
    durationInSec: number;
    onClose: VoidFunction;
    hideCloseIcon?: boolean;
    subTitle?: string;
    headerClassName?: string;
    contentClassName?: string;
}

function SuccessBSContent(props: Omit<SuccessProps, "hideCloseIcon">): ReactElement {
    const { title, durationInSec, onClose, subTitle, headerClassName, contentClassName } = props;
    const { duration } = useRef({
        duration: durationInSec,
        startAt: 0,
    }).current;

    const { elapsedTime } = useElapsedTime({
        isPlaying: true,
        options: {
            duration: durationInSec,
            onComplete: (): void => {
                onClose();
            },
            startAt: 0,
        },
    });

    const headerClass = cx(headerClassName, styles.successBsRedirectHeader);
    const contentClass = cx(contentClassName, styles.successBsRedirectContent);

    return (
        <div>
            <div className={headerClass}>
                <PaymentMethodIcon
                    parentClassName={styles.successBsRedirectIcon}
                    imgClassName={styles.successBsRedirectImg}
                    src={getCloudinaryUrl("PaymentLogos/paymentIcons/tick")}
                    width={ICON_WIDTH}
                />
            </div>

            <div className={contentClass}>
                <div className={styles.successBsRedirectContentText} data-testid={SUCCESS_BS_REDIRECT_TEST_IDS.TITLE}>
                    {title}
                </div>
                {subTitle && <div className={styles.successBsRedirectContentSubtitle}>{subTitle}</div>}
                <div
                    className={styles.successBsRedirectContentFooter}
                    data-testid={SUCCESS_BS_REDIRECT_TEST_IDS.TIMER_TEXT}
                >{`Redirecting in ${Math.ceil(duration - elapsedTime)} secs...`}</div>
            </div>
        </div>
    );
}

function SuccessBS(props: SuccessProps): ReactElement {
    const { title, durationInSec, onClose, hideCloseIcon = false, subTitle, contentClassName, headerClassName } = props;

    return (
        <BottomSheet onClose={onClose} hideOnOverlayClick={false} hideCloseIcon={hideCloseIcon}>
            {({ close }: BottomSheetChildrenProps): React.ReactElement => (
                <SuccessBSContent
                    title={title}
                    durationInSec={durationInSec}
                    onClose={close}
                    subTitle={subTitle}
                    contentClassName={contentClassName}
                    headerClassName={headerClassName}
                />
            )}
        </BottomSheet>
    );
}

export default memo(SuccessBS);
